import ContentHeader from '../components/ContentHeader';
import IconDownChevron from '../icons/IconDownChevron';
import { ICustomField, useCart } from '../hooks/useCart';
import NextToEvent from '../components/NextToEvent';
import Steps from '../components/Steps';
import MobileBottomBar from '../components/MobileBottomBar';
import { addReservation } from '../../openapi/reservations/reservations';
import ContactFields from './ContactFields';
import { useLang } from '../hooks/useLanguage';
import { ShowSuccessResponseResponse } from '../../openapi/model';
import { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import { setLanguage } from '../../openapi/custom-axios-instance';
import { useMarketing } from '../hooks/useMarketing.tsx';
import CouponInput from '../components/CouponInput';
import { EMAIL_REGEX } from '../helpers/validation';
import { getNormalizedShowId } from '../helpers/show';
import { useVerificationPreflight } from '../hooks/useVerificationPreflight';
import { buildGa4ItemsFromCart } from '../helpers/ga4Items';
import { resolveAffiliateCode } from '../helpers/affiliateCode';

const CURRENT_STEP = 4;

function Step4({
    setStep,
    show,
    setReservationUrlParams,
    stepNeedsStep3 = false,
    autoSeatingSkipStep3 = false,
    delayedSeatAssignmentEnabled = false,
    onOpenWaitlist,
}: {
    setStep: (step: number) => void;
    show: ShowSuccessResponseResponse;
    setReservationUrlParams: (reservationId: number, reservationHash: string) => void;
    stepNeedsStep3?: boolean;
    autoSeatingSkipStep3?: boolean;
    delayedSeatAssignmentEnabled?: boolean;
    onOpenWaitlist: () => void;
}) {
    const [error, setError] = useState<string | null>(null);
    const {
        genericBookingFields,
        setGenericBookingFields,
        timeslot,
        cart,
        seats,
        setSeats,
        getFinalPrice,
        getFinalPriceAfterAdjustment,
        appliedCoupon,
    } = useCart();
    const { getI18n, t, language } = useLang();
    const [smsNotification, setSmsNotification] = useState<boolean>(false);
    const [seatingGroupRequest, setSeatingGroupRequest] = useState<boolean>(false);
    const [seatingGroupNote, setSeatingGroupNote] = useState<string>('');
    const { addShippingInfo, purchase } = useMarketing();
    const normalizedShowId = getNormalizedShowId(show);
    // Show-level opt-in: the "seat me with friends" request is only offered when
    // the show enables it (default off).
    const allowSeatingGroupRequest = !!show?.marketing?.allowSeatingGroupRequest;

    // unique custom fields
    const allCustomFields: ICustomField[] = [];
    cart.map((c) => c.customFields).forEach((fields) => {
        if (fields && fields.length > 0) {
            allCustomFields.push(...fields);
        }
    });
    // deduplicate custom fields by name, type, and description, but keep the one with required=true if any
    const uniqueCustomFields: ICustomField[] = [];
    allCustomFields.forEach((field) => {
        const existingIndex = uniqueCustomFields.findIndex(
            (f) =>
                f.name === field.name &&
                f.type === field.type &&
                // TODO: should check everything? (description, description_i18n and name_i18n)
                JSON.stringify(f.description) === JSON.stringify(field.description),
        );
        if (existingIndex === -1) {
            uniqueCustomFields.push(field);
        } else if (field.required && !uniqueCustomFields[existingIndex].required) {
            uniqueCustomFields[existingIndex] = field;
        }
    });

    // state to manage custom fields with values
    const [customFieldValues, setCustomFieldValues] = useState(
        uniqueCustomFields.map((field, index) => ({
            index,
            name: field.name,
            value: field.type === 'dropdown' ? field.description[0] : '',
        })),
    );

    const handleCustomFieldChange = (fieldIndex: number, value: string) => {
        setCustomFieldValues((prev) =>
            prev.map((field) => (field.index === fieldIndex ? { ...field, value } : field)),
        );
    };

    const isAvailabilityError = (message: string): boolean => {
        const normalizedMessage = message.toLowerCase();
        const knownPatterns = [
            t('errors.blockLimitExceeded'),
            t('errors.notEnoughSeatsProvided'),
            t('widgetInfo.invalidSuggestion'),
            'block limit exceeded',
            'not enough seats',
            'not enough tickets',
            'sold out',
        ]
            .filter(Boolean)
            .map((text) => text.toLowerCase());

        return knownPatterns.some((pattern) => normalizedMessage.includes(pattern));
    };

    const onSubmit = async () => {
        try {
            // set the language for the api call before making the call
            setLanguage(language);

            // Read UTM params from localStorage for attribution
            let source: Record<string, string> | undefined;
            try {
                const raw = window.localStorage.getItem('utmParams');
                if (raw) {
                    source = JSON.parse(raw);
                }
            } catch {
                // ignore parse errors
            }

            const affiliateCode = resolveAffiliateCode();

            // calculate showFees
            const res = await addReservation({
                showId: show.id as number,
                seats: seats.map((seat) => ({
                    id: seat.id,
                    ticketTypeId: seat.ticketTypeId,
                })),
                genericBookingFields,
                timeslotId: timeslot!.id,
                cart: cart.map((ticket) => ({
                    ticketTypeId: ticket.ticketTypeId,
                    ticketOptionId: ticket.ticketOptionId,
                    quantity: ticket.quantity,
                })),
                // answers from customFieldValues
                answers: customFieldValues.map((field) => ({
                    label: field.name,
                    value: field.value,
                })),
                priceShownToUser: getFinalPriceAfterAdjustment(show.fees),
                smsNotification,
                couponCode: appliedCoupon?.code,
                source,
                affiliateCode,
                seatingGroupRequest: seatingGroupRequest || undefined,
                seatingGroupNote:
                    seatingGroupRequest && seatingGroupNote ? seatingGroupNote : undefined,
            });

            const cartItems = buildGa4ItemsFromCart(cart, show.title);
            if (cartItems.length > 0) {
                addShippingInfo({
                    currency: 'USD',
                    value: getFinalPriceAfterAdjustment(show.fees) / 100,
                    shipping_tier: 'customer_details',
                    items: cartItems,
                });
                purchase({
                    items: cartItems,
                    value: getFinalPriceAfterAdjustment(show.fees) / 100,
                    currency: 'USD',
                    transaction_id: `res-${res.reservationId}`,
                });
            }

            // Clear UTM params after successful booking
            try {
                window.localStorage.removeItem('utmParams');
            } catch {
                // ignore
            }

            setReservationUrlParams(res.reservationId, res.reservationHash);
        } catch (submitError: any) {
            console.error('Error creating reservation:', submitError);

            const apiMessage: string | undefined = submitError?.response?.data?.message;

            if (apiMessage && isAvailabilityError(apiMessage)) {
                onOpenWaitlist();
                return;
            }

            if (apiMessage) {
                setError(apiMessage);
                return;
            }

            setError(
                submitError instanceof Error
                    ? submitError.message
                    : t('errors.createReservationError'),
            );
        }
    };

    // if no tickets needed, we should drop tickets on this screen
    useEffect(() => {
        if (!stepNeedsStep3 && seats.length > 0) {
            setSeats([]);
        }
    }, [stepNeedsStep3, setSeats, seats.length]);

    // check if any of the required fields or the custom fields are empty
    const isContactInfoIncomplete =
        !EMAIL_REGEX.test(genericBookingFields.email) ||
        !genericBookingFields.firstName ||
        !genericBookingFields.lastName ||
        !genericBookingFields.email ||
        !genericBookingFields.phone;
    const isAnyRequiredCustomFieldEmpty = uniqueCustomFields.some(
        (field, idx) =>
            field.required && !customFieldValues.find((f) => f.index === idx)?.value?.trim(),
    );
    const isTimeslotInPast = timeslot ? new Date(timeslot.startTime) < new Date() : true;

    const verification = useVerificationPreflight({
        showId: show.id as number | undefined,
        timeslotId: timeslot?.id,
        hasVerificationScript: !!show.hasVerificationScript,
        language,
    });

    const disabledNextButton =
        isContactInfoIncomplete ||
        isAnyRequiredCustomFieldEmpty ||
        isTimeslotInPast ||
        verification.status === 'error' ||
        verification.status === 'loading' ||
        (seatingGroupRequest && !seatingGroupNote.trim());

    // Surface every reason the CTA is disabled — guests were getting a
    // greyed-out button with no hint about what was missing.
    const disabledReasons: string[] = [];
    if (verification.status === 'loading') {
        disabledReasons.push(t('errors.verifying'));
    }
    if (verification.status === 'error' && verification.errorMessage) {
        disabledReasons.push(verification.errorMessage);
    }
    if (isTimeslotInPast) {
        disabledReasons.push(t('errors.timeslotInPast'));
    }
    if (!genericBookingFields.firstName) {
        disabledReasons.push(
            t('errors.fieldRequired', { field: t('widgetBookingInformation.firstName') }),
        );
    }
    if (!genericBookingFields.lastName) {
        disabledReasons.push(
            t('errors.fieldRequired', { field: t('widgetBookingInformation.lastName') }),
        );
    }
    if (!genericBookingFields.email) {
        disabledReasons.push(
            t('errors.fieldRequired', { field: t('widgetBookingInformation.email') }),
        );
    } else if (!EMAIL_REGEX.test(genericBookingFields.email)) {
        disabledReasons.push(t('widgetBookingInformation.emailError'));
    }
    if (!genericBookingFields.phone) {
        disabledReasons.push(
            t('errors.fieldRequired', { field: t('widgetBookingInformation.phone') }),
        );
    }
    uniqueCustomFields.forEach((field, idx) => {
        if (field.required && !customFieldValues.find((f) => f.index === idx)?.value?.trim()) {
            disabledReasons.push(t('errors.customFieldRequired', { field: field.name }));
        }
    });
    if (seatingGroupRequest && !seatingGroupNote.trim()) {
        disabledReasons.push(t('widgetBookingInformation.seatingGroupNoteRequired'));
    }

    if (error) {
        // we should show the error and a large button to go back to the cart and retry
        return (
            <div className="fireknife-step fireknife-step4">
                <div className="fireknife-step__main fireknife-step4__main">
                    <div className="fireknife-step4__content-header">
                        <ContentHeader
                            prevStep={() => {
                                setError(null);
                            }}
                            label={t('widgetStepNavigation.step5retry')}
                        />
                    </div>
                    <div className="fireknife-booking__summary">
                        <h2 className="fireknife-booking__summary-title fireknife-booking-title">
                            {t('errors.createReservationError')}
                        </h2>
                        <div className="fireknife-booking__summary-desc fireknife-booking-error-title">
                            {t('errors.createReservationErrorDetails')}
                        </div>
                        <div className="fireknife-booking__summary-desc fireknife-booking-error">
                            {error}
                        </div>
                    </div>
                </div>
                <aside className="fireknife-step__aside fireknife-step4__aside">
                    <MobileBottomBar
                        showFees={show.fees}
                        isBreakingOutFees={show.isBreakingOutFees}
                        priceListingMode={show.priceListingMode}
                        cardProcessingFee={show.cardProcessingFee}
                        nextStep={onSubmit}
                        label={t('widgetStepNavigation.step4next')}
                        disabled={disabledNextButton}
                        verification={verification}
                    />
                    <div className="fireknife-step__aside-content">
                        <div className="fireknife-step4__payment">
                            <Steps
                                step={4}
                                setStep={setStep}
                                showId={normalizedShowId}
                                delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                                rentalMode={!!show?.marketing?.rentalMode}
                            ></Steps>
                        </div>
                    </div>
                    <NextToEvent
                        hasNextIcon={false}
                        nextStep={() => {
                            setError(null);
                        }}
                        label={t('widgetStepNavigation.step5retry')}
                        disabled={disabledNextButton}
                        showFees={show.fees}
                        isBreakingOutFees={show.isBreakingOutFees}
                        priceListingMode={show.priceListingMode}
                        cardProcessingFee={show.cardProcessingFee}
                    />
                </aside>
            </div>
        );
    }

    return (
        <div className="fireknife-step fireknife-step4">
            <div className="fireknife-step__main fireknife-step4__main">
                <div className="fireknife-step4__content-header">
                    <ContentHeader
                        prevStep={() => {
                            if (autoSeatingSkipStep3 === true) {
                                setStep(CURRENT_STEP - 2);
                            } else {
                                setStep(CURRENT_STEP - 1);
                            }
                        }}
                        label={
                            stepNeedsStep3
                                ? t('widgetStepNavigation.step4back')
                                : t('widgetStepNavigation.step3back')
                        }
                    />
                </div>
                <div className="fireknife-step4__select-seats">
                    <h2 className="fireknife-booking-title">
                        {t('widgetBookingInformation.contactInformation')}
                    </h2>

                    <ContactFields
                        values={genericBookingFields}
                        setValues={setGenericBookingFields}
                        emailError={
                            genericBookingFields.email.length > 0 &&
                            !EMAIL_REGEX.test(genericBookingFields.email)
                        }
                    />
                    {uniqueCustomFields.length > 0 && (
                        <>
                            <div className="fireknife-form-groups fireknife-form-groups--other-info">
                                {uniqueCustomFields.map((field, fieldIndex) => (
                                    <div key={fieldIndex} className="fireknife-form-group">
                                        <label
                                            className={`fireknife-form-label ${
                                                field.required
                                                    ? 'fireknife-form-label--required'
                                                    : ''
                                            }`}
                                        >
                                            {getI18n(field.name, field.name_i18n)}
                                        </label>
                                        {field.type === 'multi' ? (
                                            <div className="fireknife-form-control-box">
                                                <textarea
                                                    rows={3}
                                                    value={
                                                        customFieldValues.find(
                                                            (f) => f.index === fieldIndex,
                                                        )?.value || ''
                                                    }
                                                    onChange={(e) =>
                                                        handleCustomFieldChange(
                                                            fieldIndex,
                                                            e.target.value,
                                                        )
                                                    }
                                                    required={field.required}
                                                    className="fireknife-form-control"
                                                />
                                            </div>
                                        ) : field.type === 'dropdown' &&
                                          Array.isArray(field.description) ? (
                                            <>
                                                <div className="fireknife-form-control-box">
                                                    <select
                                                        className="fireknife-form-control fireknife-form-control--select"
                                                        value={
                                                            customFieldValues.find(
                                                                (f) => f.index === fieldIndex,
                                                            )?.value || ''
                                                        }
                                                        onChange={(e) =>
                                                            handleCustomFieldChange(
                                                                fieldIndex,
                                                                e.target.value,
                                                            )
                                                        }
                                                        required={field.required}
                                                    >
                                                        {field.description.map(
                                                            (
                                                                option: string,
                                                                optionIndex: number,
                                                            ) => (
                                                                <option
                                                                    key={optionIndex}
                                                                    value={option}
                                                                >
                                                                    {getI18n(
                                                                        option,
                                                                        field.description_i18n,
                                                                        optionIndex,
                                                                    )}
                                                                </option>
                                                            ),
                                                        )}
                                                    </select>
                                                    <IconDownChevron className="fireknife-datepicker__timeslot-select-icon" />
                                                </div>
                                            </>
                                        ) : (
                                            <input
                                                type="text"
                                                value={
                                                    customFieldValues.find(
                                                        (f) => f.index === fieldIndex,
                                                    )?.value || ''
                                                }
                                                onChange={(e) =>
                                                    handleCustomFieldChange(
                                                        fieldIndex,
                                                        e.target.value,
                                                    )
                                                }
                                                required={field.required}
                                                className="fireknife-form-control"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    {show.notificationChannels?.includes('sms') && (
                        <div className="fireknife-form-group fireknife-form-group--checkbox">
                            <label className="fireknife-form-label fireknife-form-label--checkbox">
                                <input
                                    type="checkbox"
                                    checked={smsNotification}
                                    onChange={(e) => setSmsNotification(e.target.checked)}
                                    className="fireknife-form-checkbox"
                                />
                                <span>{t('widgetBookingInformation.smsNotificationInfo')}</span>
                            </label>
                        </div>
                    )}

                    {allowSeatingGroupRequest && (
                        <>
                            <div className="fireknife-form-group fireknife-form-group--checkbox">
                                <label className="fireknife-form-label fireknife-form-label--checkbox">
                                    <input
                                        type="checkbox"
                                        checked={seatingGroupRequest}
                                        onChange={(e) => {
                                            setSeatingGroupRequest(e.target.checked);
                                            if (!e.target.checked) setSeatingGroupNote('');
                                        }}
                                        className="fireknife-form-checkbox"
                                    />
                                    <span>
                                        {t('widgetBookingInformation.seatingGroupCheckbox')}
                                    </span>
                                </label>
                            </div>
                            {seatingGroupRequest && (
                                <div className="fireknife-seating-group">
                                    <p className="fireknife-seating-group__description">
                                        {t('widgetBookingInformation.seatingGroupDescription')}
                                    </p>
                                    <span className="fireknife-seating-group__label">
                                        {t('widgetBookingInformation.seatingGroupNoteLabel')}
                                    </span>
                                    <div className="fireknife-form-control-box">
                                        <input
                                            type="text"
                                            value={seatingGroupNote}
                                            onChange={(e) => setSeatingGroupNote(e.target.value)}
                                            aria-label={t(
                                                'widgetBookingInformation.seatingGroupNoteLabel',
                                            )}
                                            placeholder={t(
                                                'widgetBookingInformation.seatingGroupNotePlaceholder',
                                            )}
                                            className="fireknife-form-control"
                                            maxLength={500}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <h2 className="fireknife-booking-title" style={{ marginTop: '24px' }}>
                        {t('coupons.title')}
                    </h2>
                    <CouponInput
                        showId={show.id as number}
                        email={genericBookingFields.email}
                        orderAmount={getFinalPrice(show.fees)}
                    />
                </div>
                <Footer />
            </div>
            <aside className="fireknife-step__aside fireknife-step4__aside">
                <MobileBottomBar
                    showFees={show.fees}
                    isBreakingOutFees={show.isBreakingOutFees}
                    priceListingMode={show.priceListingMode}
                    cardProcessingFee={show.cardProcessingFee}
                    nextStep={onSubmit}
                    label={t('widgetStepNavigation.step4next')}
                    disabled={disabledNextButton}
                    verification={verification}
                    disabledReasons={disabledReasons}
                />
                <div className="fireknife-step__aside-content">
                    <div className="fireknife-step4__payment">
                        <Steps
                            step={4}
                            setStep={setStep}
                            showId={normalizedShowId}
                            delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                            rentalMode={!!show?.marketing?.rentalMode}
                        ></Steps>
                    </div>
                </div>
                <NextToEvent
                    nextStep={onSubmit}
                    label={t('widgetStepNavigation.step4next')}
                    disabled={disabledNextButton}
                    showFees={show.fees}
                    isBreakingOutFees={show.isBreakingOutFees}
                    priceListingMode={show.priceListingMode}
                    cardProcessingFee={show.cardProcessingFee}
                    verification={verification}
                    disabledReasons={disabledReasons}
                />
            </aside>
        </div>
    );
}

export default Step4;
